import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Info,
  LUCIDE_ICONS,
  LucideAngularModule,
  LucideIconProvider,
  Mail,
  Megaphone,
  RefreshCw,
  Users,
} from 'lucide-angular';
import { Novedad, RolPlataforma } from '../../../../core/models/novedades.models';
import { RoleNovedadesPresenter } from '../../../../core/presenters/role-novedades.presenter';
import { NovedadesService } from '../../../../core/services/novedades.service';
import { formatNotificationDate, formatShortDate } from '../../../../core/utils/date-format.util';

type EstadoLecturaFiltro = 'todas' | 'no_leidas' | 'leidas';
type TipoNovedadFiltro = 'todos' | 'informacion' | 'completado' | 'alerta' | 'pendiente';
type TipoVisualNovedad = Exclude<TipoNovedadFiltro, 'todos'>;

interface GrupoNotificaciones {
  titulo: string;
  tipo: 'urgente' | 'fecha';
  items: Novedad[];
}

@Component({
  selector: 'app-actualizaciones-panel',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  providers: [
    {
      provide: LUCIDE_ICONS,
      multi: true,
      useValue: new LucideIconProvider({
        AlertTriangle,
        Bell,
        Calendar,
        CheckCircle,
        Clock,
        FileText,
        Info,
        Mail,
        Megaphone,
        RefreshCw,
        Users,
      }),
    },
  ],
  templateUrl: './actualizaciones-panel.html',
  styleUrl: './actualizaciones-panel.css',
})
export class ActualizacionesPanel extends RoleNovedadesPresenter implements OnInit, OnChanges {
  @Input() rol: RolPlataforma = 'postulante';

  readonly opcionesCantidad = [3, 10, 20, 30, 40, 50];
  cantidadPorPagina = 3;
  paginaActual = 1;
  cargando = false;
  estadoLecturaFiltro: EstadoLecturaFiltro = 'todas';
  tipoFiltro: TipoNovedadFiltro = 'todos';

  constructor(
    novedadesService: NovedadesService,
    private readonly router: Router
  ) {
    super(novedadesService);
  }

  ngOnInit(): void {
    this.novedadesService.notificaciones$.subscribe((items) => {
      this.novedades = items;
    });
    this.recargar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rol'] && !changes['rol'].firstChange) {
      this.paginaActual = 1;
      this.recargar();
    }
  }

  get cantidadNoLeidas(): number {
    return this.novedades.filter((item) => !item.leida).length;
  }

  get cantidadLeidas(): number {
    return this.novedades.filter((item) => item.leida).length;
  }

  get cantidadUrgentes(): number {
    return this.novedades.filter((item) => !item.leida && this.tipoVisualNovedad(item) === 'alerta').length;
  }

  get cantidadPendientes(): number {
    return this.novedades.filter((item) => !item.leida && this.tipoVisualNovedad(item) === 'pendiente').length;
  }

  get cantidadInformativas(): number {
    return this.novedades.filter((item) => this.tipoVisualNovedad(item) === 'informacion').length;
  }

  get novedadesFiltradas(): Novedad[] {
    return this.novedades.filter((item) => {
      if (this.estadoLecturaFiltro === 'no_leidas') return !item.leida;
      if (this.estadoLecturaFiltro === 'leidas') return item.leida;
      return true;
    });
  }

  get notificacionesPaginadas(): Novedad[] {
    const paginaTotal = this.totalPaginas;
    if (this.paginaActual > paginaTotal) {
      this.paginaActual = paginaTotal;
    }

    const inicio = (this.paginaActual - 1) * this.cantidadPorPagina;
    return this.novedadesFiltradas.slice(inicio, inicio + this.cantidadPorPagina);
  }

  get paginasArray(): number[] {
    const total = this.totalPaginas;
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  get gruposNotificaciones(): GrupoNotificaciones[] {
    const urgentes = this.notificacionesPaginadas.filter((item) => !item.leida && this.tipoVisualNovedad(item) === 'alerta');
    const restantes = this.notificacionesPaginadas.filter((item) => item.leida || this.tipoVisualNovedad(item) !== 'alerta');
    const grupos: GrupoNotificaciones[] = [];

    if (urgentes.length > 0) {
      grupos.push({ titulo: 'Requieren atencion', tipo: 'urgente', items: urgentes });
    }

    for (const item of restantes) {
      const titulo = this.grupoFechaNovedad(item.fecha);
      const grupoExistente = grupos.find((grupo) => grupo.tipo === 'fecha' && grupo.titulo === titulo);

      if (grupoExistente) {
        grupoExistente.items.push(item);
      } else {
        grupos.push({ titulo, tipo: 'fecha', items: [item] });
      }
    }

    return grupos;
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.novedadesFiltradas.length / this.cantidadPorPagina));
  }

  cambiarCantidadPorPagina(): void {
    this.paginaActual = 1;
  }

  cambiarPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPaginas) {
      this.paginaActual = pagina;
    }
  }

  cambiarCantidadDesdePaginador(cantidad: number): void {
    this.cantidadPorPagina = cantidad;
    this.cambiarCantidadPorPagina();
  }

  cambiarEstadoLectura(filtro: EstadoLecturaFiltro): void {
    this.estadoLecturaFiltro = filtro;
    this.paginaActual = 1;
  }

  cambiarTipoFiltro(): void {
    this.paginaActual = 1;
  }

  recargar(): void {
    this.cargando = true;
    this.novedadesService.getNovedadesPorRol(this.rol).subscribe((data) => {
      this.novedades = data;
      this.cargando = false;
    });
  }

  marcarTodasComoLeidas(): void {
    this.novedadesService.marcarTodasComoLeidas();
  }

  abrirDetalle(novedad: Novedad): void {
    if (!novedad.leida) {
      this.novedadesService.marcarComoLeida(novedad.id);
    }
    if (novedad.accionRuta) {
      this.router.navigate([novedad.accionRuta], {
        queryParams: novedad.accionQueryParams,
      });
    } else {
      this.router.navigate(['/postulaciones'], {
        queryParams: { role: this.rol },
      });
    }
  }

  formatoFecha(fecha?: string): string {
    return formatNotificationDate(fecha);
  }

  fechaNovedad(fecha?: string): string {
    return this.formatoFecha(fecha);
  }

  puedeMarcarTodasComoLeidas(): boolean {
    return this.cantidadNoLeidas > 0;
  }

  categoriaNovedad(novedad: Novedad): string {
    const titulo = novedad.titulo.toLowerCase();

    if (titulo.includes('convocatoria')) return 'Nueva convocatoria';
    if (titulo.includes('cierre')) return 'Proxima a cerrar';
    if (titulo.includes('invit')) return 'Invitacion';
    if (titulo.includes('patrocinio')) return 'Patrocinio';
    if (titulo.includes('mantenimiento')) return 'Sistema';
    if (titulo.includes('guia') || titulo.includes('documento')) return 'Notificacion';
    if (titulo.includes('pendiente')) return 'Postulacion pendiente';

    return this.etiquetaNovedad(this.tipoVisualNovedad(novedad));
  }

  iconoNovedad(novedad: Novedad): string {
    const tipo = this.tipoVisualNovedad(novedad);

    if (tipo === 'alerta') return 'alert-triangle';
    if (tipo === 'completado') return 'check-circle';
    if (tipo === 'pendiente') return 'clock';

    const titulo = novedad.titulo.toLowerCase();
    if (titulo.includes('convocatoria')) return 'megaphone';
    if (titulo.includes('invit')) return 'users';
    if (titulo.includes('documento') || titulo.includes('guia')) return 'file-text';
    return 'info';
  }

  contextoNovedad(novedad: Novedad): string | null {
    const contenido = `${novedad.titulo} ${novedad.detalle ?? ''}`;

    if (contenido.includes('FONDECYT')) return 'FONDECYT';
    if (contenido.includes('FONDEF')) return 'FONDEF';
    if (contenido.includes('Capital Humano')) return 'Capital Humano';
    if (contenido.includes('patrocinio')) return 'Patrocinio';
    return null;
  }

  referenciaNovedad(novedad: Novedad): string | null {
    const contenido = `${novedad.titulo} ${novedad.detalle ?? ''}`;
    const match = contenido.match(/POST-\d{4}-\d+/i);
    return match ? match[0].toUpperCase() : null;
  }

  tipoVisualNovedad(novedad: Novedad): TipoVisualNovedad {
    return novedad.tipo ?? 'informacion';
  }

  etiquetaNovedad(tipo: TipoVisualNovedad): string {
    if (tipo === 'informacion') return 'Informativa';
    if (tipo === 'completado') return 'Completada';
    if (tipo === 'alerta') return 'Urgente';
    return 'Pendiente';
  }

  estadoLecturaLabel(novedad: Novedad): string {
    return novedad.leida ? 'Leida' : 'No leida';
  }

  detalleNovedad(novedad: Novedad): string {
    return novedad.detalle ?? novedad.titulo;
  }

  grupoFechaNovedad(fecha?: string): string {
    const fechaBase = this.obtenerFecha(fecha);
    if (!fechaBase) return 'Sin fecha';

    const hoy = this.normalizarDia(new Date());
    const fechaNormalizada = this.normalizarDia(fechaBase);
    const diferenciaDias = Math.round((hoy.getTime() - fechaNormalizada.getTime()) / 86_400_000);

    if (diferenciaDias === 0) return 'Hoy';
    if (diferenciaDias === 1) return 'Ayer';
    return 'Anteriores';
  }

  private obtenerFecha(fecha?: string): Date | null {
    if (!fecha) return null;

    if (!fecha.includes('T')) {
      const [anio, mes, dia] = fecha.split('-').map((valor) => Number(valor));
      if (!anio || !mes || !dia) return null;
      return new Date(anio, mes - 1, dia);
    }

    const parsed = new Date(fecha);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private normalizarDia(fecha: Date): Date {
    return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
  }
}
